import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import UserLayout from '@/components/Layout/UserLayout';
import { showSpinner, hideSpinner } from '@/components/Layout/LoadingSpinner';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { Badge, Button, Callout, Card, StatusBadge } from '@/components/UI';
import FormInput from '@/components/UI/FormInput';
import FormNumberInput from '@/components/UI/FormNumberInput';
import FormSelect from '@/components/UI/FormSelect';
import FormSwitch from '@/components/UI/FormSwitch';
import ConfirmationDialog from '@/components/UI/ConfirmationDialog';
import OptionSearchConfigForm from './OptionSearchConfigForm';
import {
    DEFAULT_OPTION_SEARCH_FORM_STATE,
    formStateToOptionSearchConfig,
    optionSearchConfigToFormState,
    validateOptionSearchFormState,
    type OptionSearchConfigFormState
} from './optionSearchConfigFormState';

import { trpc } from '@/api/trpc';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { useAppQuery } from '@/hooks/useAppQuery';
import { DEFAULT_REQUISITIES_STALE_TIME } from '@/config/constants';
import type { OptionBuyingStrategy } from '@einhaus/project314-types';
import ResourceAccessNotice from '@/components/resourceAccess/ResourceAccessNotice';
import ShareResourceModal from '@/components/resourceAccess/ShareResourceModal';

type BuyingMethod = 'fixedPrice' | 'inTheMoney' | 'outOfTheMoney' | 'atTheMoney' | 'optionSearchConfig';
type OpenTransactionType = 'buyToOpen' | 'sellToOpen';

const BUYING_METHOD_OPTIONS: { value: BuyingMethod; title: string; description: string; advanced?: boolean }[] = [
    { value: 'atTheMoney', title: 'At the Money', description: 'Strike closest to the underlying price.' },
    { value: 'inTheMoney', title: 'In the Money', description: 'Strike a target % in the money.' },
    { value: 'outOfTheMoney', title: 'Out of the Money', description: 'Strike a target % out of the money.' },
    { value: 'fixedPrice', title: 'Fixed Premium', description: 'Option whose premium is closest to a target price.' },
    {
        value: 'optionSearchConfig',
        title: 'Option Search',
        description: 'Filter and rank the entire chain by custom criteria.',
        advanced: true
    }
];

interface FormData {
    name: string;
    buyingMethod: BuyingMethod;
    openTransactionType: OpenTransactionType;
    buyingMethodValue: number | null;
    minimumDaysToExpiry: number | null;
    maximumDaysToExpiry: number | null;
    targetDaysToExpiry: number | null;
    minimumPrice: number | null;
    maximumSpreadPercentage: number | null;
    optionSearchConfig: OptionSearchConfigFormState;
    indexOptionResearchScope: 'spxSpxw' | 'fullSize' | 'mini' | null;
    autoBacktest: boolean;
    active: boolean;
    isExperimentOnly: boolean;
    entryDuration: string;
    timeHorizon: string;
}

const createDefaultFormData = (): FormData => ({
    name: '',
    buyingMethod: 'atTheMoney',
    openTransactionType: 'buyToOpen',
    buyingMethodValue: null,
    minimumDaysToExpiry: null,
    maximumDaysToExpiry: null,
    targetDaysToExpiry: null,
    minimumPrice: null,
    maximumSpreadPercentage: null,
    optionSearchConfig: DEFAULT_OPTION_SEARCH_FORM_STATE,
    indexOptionResearchScope: null,
    autoBacktest: false,
    active: true,
    isExperimentOnly: false,
    entryDuration: '',
    timeHorizon: ''
});

const OptionBuyingStrategyEditor: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const copyFromId = searchParams.get('copyFrom');

    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const isCreateMode = id === 'new';
    const isEditMode = !!id && id !== 'new';
    const strategyId = isEditMode ? Number(id) : null;
    const copyFromStrategyId = isCreateMode && copyFromId ? Number(copyFromId) : null;

    const [error, setError] = useState<string | null>(null);
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);

    const [formData, setFormData] = useState<FormData>(createDefaultFormData);
    const [savedSnapshot, setSavedSnapshot] = useState<string>(() => JSON.stringify(createDefaultFormData()));

    const strategyQuery = useAppQuery(
        {
            ...trpc.strategy.fetchOptionBuyingStrategyById.queryOptions({ id: strategyId ?? 0 }),
            enabled: !!strategyId,
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load option buying strategy',
            setError: (msg) => setError(msg),
            showErrorToast: true
        }
    );

    const copySourceQuery = useAppQuery(
        {
            ...trpc.strategy.fetchOptionBuyingStrategyById.queryOptions({ id: copyFromStrategyId ?? 0 }),
            enabled: !!copyFromStrategyId,
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load source strategy for copy',
            setError: (msg) => setError(msg),
            showErrorToast: true
        }
    );

    const accessSummaryQuery = useQuery({
        ...trpc.resourceAccess.getAccessSummary.queryOptions({
            resourcePk: strategyId ?? 0,
            resourceType: 'optionBuyingStrategy'
        }),
        enabled: !!strategyId
    });

    const loadedStrategy: OptionBuyingStrategy | null = useMemo(() => {
        if (isEditMode) return strategyQuery.data?.strategy ?? null;
        if (copyFromStrategyId) return copySourceQuery.data?.strategy ?? null;
        return null;
    }, [isEditMode, strategyQuery.data, copySourceQuery.data, copyFromStrategyId]);

    const isOwnedStrategy = !isEditMode || accessSummaryQuery.data?.ownedByUser === true;
    const canEditStrategy = !isEditMode || accessSummaryQuery.data?.canEdit === true;

    useEffect(() => {
        const isLoading = strategyQuery.isLoading || copySourceQuery.isLoading;
        if (isLoading) showSpinner();
        else hideSpinner();

        return () => hideSpinner();
    }, [strategyQuery.isLoading, copySourceQuery.isLoading]);

    useEffect(() => {
        if (!loadedStrategy) return;

        const nextFormData: FormData = {
            name: isCreateMode ? `${loadedStrategy.name ?? ''} (Copy)` : (loadedStrategy.name ?? ''),
            buyingMethod: loadedStrategy.buyingMethod,
            openTransactionType: loadedStrategy.openTransactionType,
            buyingMethodValue: loadedStrategy.buyingMethodValue,
            minimumDaysToExpiry: loadedStrategy.minimumDaysToExpiry,
            maximumDaysToExpiry: loadedStrategy.maximumDaysToExpiry,
            targetDaysToExpiry: loadedStrategy.targetDaysToExpiry,
            minimumPrice: loadedStrategy.minimumPrice,
            maximumSpreadPercentage: loadedStrategy.maximumSpreadPercentage,
            optionSearchConfig: optionSearchConfigToFormState(loadedStrategy.optionSearchConfig),
            indexOptionResearchScope: loadedStrategy.indexOptionResearchScope ?? null,
            autoBacktest: loadedStrategy.autoBacktest,
            active: isCreateMode ? false : loadedStrategy.active,
            isExperimentOnly: loadedStrategy.isExperimentOnly ?? false,
            entryDuration: loadedStrategy.entryDuration ?? '',
            timeHorizon: loadedStrategy.timeHorizon ?? ''
        };

        setFormData(nextFormData);
        setSavedSnapshot(JSON.stringify(nextFormData));
        setValidationErrors({});
        setError(null);
    }, [loadedStrategy, isCreateMode]);

    const isDirty = useMemo(() => JSON.stringify(formData) !== savedSnapshot, [formData, savedSnapshot]);

    const invalidateList = () => {
        void queryClient.invalidateQueries({ queryKey: trpc.strategy.fetchOptionBuyingStrategies.queryOptions({}).queryKey });
    };

    const createMutation = useTrpcMutation(
        trpc.strategy.createOptionBuyingStrategy.mutationOptions({
            onSuccess: (data) => {
                invalidateList();
                void navigate(`/user/optionBuyingStrategy/${data.strategy.id}`);
            }
        }),
        { successMessage: 'Option buying strategy created', fallbackErrorMessage: 'Failed to create option buying strategy' }
    );

    const updateMutation = useTrpcMutation(
        trpc.strategy.updateOptionBuyingStrategy.mutationOptions({
            onSuccess: (data) => {
                invalidateList();

                void queryClient.invalidateQueries({
                    queryKey: trpc.strategy.fetchOptionBuyingStrategyById.queryOptions({ id: data.strategy.id }).queryKey
                });
            }
        }),
        { successMessage: 'Option buying strategy saved', fallbackErrorMessage: 'Failed to save option buying strategy' }
    );

    const usesBuyingMethodValue =
        formData.buyingMethod === 'fixedPrice' || formData.buyingMethod === 'inTheMoney' || formData.buyingMethod === 'outOfTheMoney';
    const usesTargetDte =
        formData.buyingMethod === 'atTheMoney' || formData.buyingMethod === 'inTheMoney' || formData.buyingMethod === 'outOfTheMoney';

    const effectiveMinimumDays = formData.minimumDaysToExpiry ?? 5;
    const effectiveMaximumDays = formData.maximumDaysToExpiry ?? 30;
    const targetOutsideExpirationRange =
        usesTargetDte &&
        formData.targetDaysToExpiry !== null &&
        (formData.targetDaysToExpiry < effectiveMinimumDays || formData.targetDaysToExpiry > effectiveMaximumDays);

    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};

        if (!formData.name.trim()) errors.name = 'Name is required';

        if (usesBuyingMethodValue && formData.buyingMethodValue === null) {
            errors.buyingMethodValue = 'This value is required for the selected buying method';
        }

        if (formData.buyingMethod === 'optionSearchConfig') {
            const configErrors = validateOptionSearchFormState(formData.optionSearchConfig);

            for (const [field, message] of Object.entries(configErrors)) {
                errors[`optionSearchConfig.${field}`] = message;
            }
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const buildPayload = () => {
        const optionSearchConfig =
            formData.buyingMethod === 'optionSearchConfig' ? formStateToOptionSearchConfig(formData.optionSearchConfig) : null;

        return {
            name: formData.name.trim(),
            buyingMethod: formData.buyingMethod,
            openTransactionType: formData.openTransactionType,
            buyingMethodValue: usesBuyingMethodValue ? formData.buyingMethodValue : null,
            minimumDaysToExpiry: formData.minimumDaysToExpiry,
            maximumDaysToExpiry: formData.maximumDaysToExpiry,
            targetDaysToExpiry: formData.targetDaysToExpiry,
            minimumPrice: formData.minimumPrice,
            maximumSpreadPercentage: formData.maximumSpreadPercentage,
            optionSearchConfig,
            indexOptionResearchScope: formData.indexOptionResearchScope,
            autoBacktest: formData.autoBacktest,
            active: formData.active,
            isExperimentOnly: formData.isExperimentOnly,
            entryDuration: formData.entryDuration.trim() ? formData.entryDuration.trim() : null,
            timeHorizon: formData.timeHorizon.trim() ? formData.timeHorizon.trim() : null
        };
    };

    const handleSave = async () => {
        setError(null);
        if (!validateForm()) return;

        try {
            const payload = buildPayload();

            if (isCreateMode) {
                await createMutation.mutateAsync(payload);
            } else if (strategyId) {
                await updateMutation.mutateAsync({ id: strategyId, ...payload });
            }
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to save');
        }
    };

    const handleDeactivate = async () => {
        if (!strategyId) return;
        setShowDeactivateConfirm(false);

        await updateMutation.mutateAsync({ id: strategyId, active: false });
        setFormData((prev) => ({ ...prev, active: false }));
    };

    const isLoading = strategyQuery.isLoading || copySourceQuery.isLoading;

    const notFound =
        (isEditMode && strategyQuery.data && !strategyQuery.data.strategy) ||
        (copyFromStrategyId && copySourceQuery.data && !copySourceQuery.data.strategy);

    if (isLoading) {
        return (
            <UserLayout title="Option Buying Strategy - Project 314">
                <div className="flex justify-center py-10">
                    <DataLoadingSpinner />
                </div>
            </UserLayout>
        );
    }

    if (notFound) {
        return (
            <UserLayout title="Option Buying Strategy - Project 314">
                <Card>
                    <div className="text-zinc-200">
                        Strategy not found.{' '}
                        <Link className="text-blue-400 underline" to="/user/optionBuyingStrategies">
                            Back to list
                        </Link>
                    </div>
                </Card>
            </UserLayout>
        );
    }

    return (
        <UserLayout title="Option Buying Strategy - Project 314">
            <div className="flex items-center justify-between gap-3 mb-4">
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-white text-lg sm:text-xl font-semibold mb-0 truncate">
                            {isCreateMode ? 'New Option Buying Strategy' : formData.name || `Option Buying Strategy #${strategyId}`}
                        </h3>
                        {!isCreateMode && <StatusBadge status={formData.active ? 'active' : 'inactive'} />}
                        {formData.isExperimentOnly && (
                            <StatusBadge status="experiment_only" tone="accent">
                                Experiment only
                            </StatusBadge>
                        )}
                    </div>
                    {!isCreateMode && <div className="text-xs text-zinc-500 mt-0.5">Option buying strategy #{strategyId}</div>}
                </div>
                <div className="flex gap-2 shrink-0">
                    <Button to="/user/optionBuyingStrategies" variant="secondary">
                        Back
                    </Button>
                    {!isCreateMode && strategyId && (
                        <Button to={`/user/optionBuyingStrategy/new?copyFrom=${strategyId}`} variant="secondary">
                            Copy
                        </Button>
                    )}
                    {!isCreateMode && strategyId && isOwnedStrategy && (
                        <Button variant="secondary" onClick={() => setShowShareModal(true)}>
                            Share
                        </Button>
                    )}
                </div>
            </div>

            {error && (
                <Callout className="mb-4" variant="error">
                    {error}
                </Callout>
            )}

            {!isCreateMode && (
                <ResourceAccessNotice access={{ canEdit: canEditStrategy, ownedByUser: isOwnedStrategy }} noun="option buying strategy" />
            )}

            <Card>
                <fieldset className="space-y-0" disabled={!canEditStrategy}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                        <FormInput
                            error={validationErrors.name}
                            id="name"
                            label="Name"
                            value={formData.name}
                            onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                        />
                    </div>

                    <FormSelect
                        label="Index products for backtesting"
                        helpText="A product filter applies to index backtests only. Use all eligible products for mixed stock/index strategies."
                        value={formData.indexOptionResearchScope ?? ''}
                        options={[
                            { value: '', label: 'All eligible products (stocks and indices)' },
                            { value: 'fullSize', label: 'Standard index products' },
                            { value: 'mini', label: 'Mini index products (XSP, XND, MRUT)' },
                            ...(formData.indexOptionResearchScope === 'spxSpxw'
                                ? [{ value: 'spxSpxw', label: 'Saved SPX / SPXW selection' }]
                                : [])
                        ]}
                        onChange={(e) => {
                            const scope = e.target.value;
                            if (scope === '' || scope === 'fullSize' || scope === 'mini' || scope === 'spxSpxw')
                                setFormData((prev) => ({ ...prev, indexOptionResearchScope: scope || null }));
                        }}
                    />
                    <div className="mt-1">
                        <div className="text-sm font-medium text-zinc-200 mb-1" id="buyingMethodLabel">
                            Buying Method
                        </div>
                        <div
                            aria-labelledby="buyingMethodLabel"
                            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2"
                            role="radiogroup"
                        >
                            {BUYING_METHOD_OPTIONS.map((option) => {
                                const selected = formData.buyingMethod === option.value;

                                return (
                                    <button
                                        aria-checked={selected}
                                        className={`rounded-lg border p-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 disabled:cursor-not-allowed disabled:opacity-60 ${
                                            selected
                                                ? 'border-cyan-500 bg-cyan-950/40'
                                                : 'border-zinc-700 bg-black/20 hover:border-zinc-500'
                                        }`}
                                        key={option.value}
                                        role="radio"
                                        type="button"
                                        onClick={() => setFormData((prev) => ({ ...prev, buyingMethod: option.value }))}
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <span className={`text-sm font-medium ${selected ? 'text-cyan-200' : 'text-zinc-100'}`}>
                                                {option.title}
                                            </span>
                                            {option.advanced && (
                                                <Badge pill variant="secondary">
                                                    Advanced
                                                </Badge>
                                            )}
                                        </div>
                                        <div className="mt-1 text-xs text-zinc-400">{option.description}</div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {(usesBuyingMethodValue || usesTargetDte) && (
                        <div className="mt-2">
                            <div className="text-sm font-medium text-zinc-200 mb-1">Selection Settings</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                                {formData.buyingMethod === 'fixedPrice' && (
                                    <FormNumberInput
                                        error={validationErrors.buyingMethodValue}
                                        helpText="Selects the option whose premium is closest to this price."
                                        id="buyingMethodValue"
                                        label="Target Premium"
                                        min={0}
                                        prefix="$"
                                        step={0.01}
                                        value={formData.buyingMethodValue}
                                        onValueChange={(buyingMethodValue) => setFormData((prev) => ({ ...prev, buyingMethodValue }))}
                                    />
                                )}
                                {(formData.buyingMethod === 'inTheMoney' || formData.buyingMethod === 'outOfTheMoney') && (
                                    <FormNumberInput
                                        error={validationErrors.buyingMethodValue}
                                        helpText="Distance from the underlying price the target strike should sit."
                                        id="buyingMethodValue"
                                        label={formData.buyingMethod === 'inTheMoney' ? 'Target % ITM' : 'Target % OTM'}
                                        min={0}
                                        step={0.1}
                                        suffix="%"
                                        value={formData.buyingMethodValue}
                                        onValueChange={(buyingMethodValue) => setFormData((prev) => ({ ...prev, buyingMethodValue }))}
                                    />
                                )}
                                {usesTargetDte && (
                                    <FormNumberInput
                                        integer
                                        error={validationErrors.targetDaysToExpiry}
                                        helpText="Preferred days to expiration among eligible contracts. This does not change the minimum or maximum expiration limits."
                                        id="targetDaysToExpiry"
                                        label="Target days to expiration"
                                        min={0}
                                        suffix="days"
                                        value={formData.targetDaysToExpiry}
                                        onValueChange={(targetDaysToExpiry) => setFormData((prev) => ({ ...prev, targetDaysToExpiry }))}
                                    />
                                )}
                            </div>
                        </div>
                    )}

                    {formData.buyingMethod === 'optionSearchConfig' && (
                        <div className="mt-2">
                            <div className="text-sm font-medium text-zinc-200 mb-1">Option Search Config</div>
                            <OptionSearchConfigForm
                                disabled={!canEditStrategy}
                                errors={Object.fromEntries(
                                    Object.entries(validationErrors)
                                        .filter(([key]) => key.startsWith('optionSearchConfig.'))
                                        .map(([key, message]) => [key.slice('optionSearchConfig.'.length), message])
                                )}
                                state={formData.optionSearchConfig}
                                strategyMaxDte={formData.maximumDaysToExpiry}
                                strategyMaxSpreadPercentage={formData.maximumSpreadPercentage}
                                strategyMinDte={formData.minimumDaysToExpiry}
                                onChange={(updates) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        optionSearchConfig: { ...prev.optionSearchConfig, ...updates }
                                    }))
                                }
                            />
                        </div>
                    )}

                    <div className="mt-2">
                        <div className="text-sm font-medium text-zinc-200 mb-0.5">Expiration limits and execution checks</div>
                        <div className="rounded-lg border border-zinc-700 bg-black/20 px-3 py-2 mb-3" aria-live="polite">
                            <p className="text-sm text-zinc-100">
                                Expiration range used:{' '}
                                <strong>
                                    {effectiveMinimumDays}–{effectiveMaximumDays} calendar days
                                </strong>{' '}
                                from entry.
                            </p>
                            <p className="text-xs text-zinc-400 mt-1">
                                Applies to stock and index options for every buying method. Blank limits use 5 days minimum and 30 days
                                maximum. Contracts outside this range are excluded, even when no expiration is available inside it. Option
                                Search filters may narrow this range further.
                            </p>
                            {targetOutsideExpirationRange && (
                                <p className="text-xs text-amber-300 mt-1">
                                    The target expiration is outside this range. Change the limits if you want to include it.
                                </p>
                            )}
                            {effectiveMinimumDays > effectiveMaximumDays && (
                                <p className="text-xs text-amber-300 mt-1">
                                    The minimum exceeds the maximum, so this range contains no expirations.
                                </p>
                            )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-x-6">
                            <FormNumberInput
                                integer
                                error={validationErrors.minimumDaysToExpiry}
                                helpText="Calendar days from entry. Leave blank to use 5."
                                placeholder="5 (default)"
                                id="minimumDaysToExpiry"
                                label="Minimum days to expiration"
                                min={0}
                                value={formData.minimumDaysToExpiry}
                                onValueChange={(minimumDaysToExpiry) => setFormData((prev) => ({ ...prev, minimumDaysToExpiry }))}
                            />
                            <FormNumberInput
                                integer
                                error={validationErrors.maximumDaysToExpiry}
                                helpText="Calendar days from entry. Leave blank to use 30."
                                placeholder="30 (default)"
                                id="maximumDaysToExpiry"
                                label="Maximum days to expiration"
                                min={0}
                                value={formData.maximumDaysToExpiry}
                                onValueChange={(maximumDaysToExpiry) => setFormData((prev) => ({ ...prev, maximumDaysToExpiry }))}
                            />
                            <FormSelect
                                id="openTransactionType"
                                label="Opening action"
                                options={[
                                    { value: 'buyToOpen', label: 'Buy to open' },
                                    { value: 'sellToOpen', label: 'Sell to open' }
                                ]}
                                value={formData.openTransactionType}
                                onChange={(e) =>
                                    setFormData((prev) => ({ ...prev, openTransactionType: e.target.value as OpenTransactionType }))
                                }
                            />
                            <FormNumberInput
                                error={validationErrors.minimumPrice}
                                helpText="Rejects picks with a premium below this."
                                id="minimumPrice"
                                label="Minimum Price"
                                min={0}
                                prefix="$"
                                step={0.01}
                                value={formData.minimumPrice}
                                onValueChange={(minimumPrice) => setFormData((prev) => ({ ...prev, minimumPrice }))}
                            />
                            <FormNumberInput
                                integer
                                error={validationErrors.maximumSpreadPercentage}
                                helpText="Rejects picks whose bid/ask spread exceeds this % of price."
                                id="maximumSpreadPercentage"
                                label="Max Spread %"
                                min={0}
                                suffix="%"
                                value={formData.maximumSpreadPercentage}
                                onValueChange={(maximumSpreadPercentage) => setFormData((prev) => ({ ...prev, maximumSpreadPercentage }))}
                            />
                        </div>
                    </div>

                    <div className="mt-2 flex flex-wrap gap-6">
                        <FormSwitch
                            checked={formData.autoBacktest}
                            label="Auto Backtest"
                            onChange={(e) => setFormData((prev) => ({ ...prev, autoBacktest: e.target.checked }))}
                        />
                        <FormSwitch
                            checked={formData.active}
                            label="Active"
                            onChange={(e) => setFormData((prev) => ({ ...prev, active: e.target.checked }))}
                        />
                    </div>

                    <div className="mt-2">
                        <div className="text-sm font-medium text-zinc-200 mb-1">Experiment Metadata</div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6">
                            <FormSwitch
                                checked={formData.isExperimentOnly}
                                label="Experiment Only"
                                onChange={(e) => setFormData((prev) => ({ ...prev, isExperimentOnly: e.target.checked }))}
                            />
                            <FormInput
                                helpText="e.g., 1m, 15m, 1d"
                                id="entryDuration"
                                label="Entry Duration"
                                value={formData.entryDuration}
                                onChange={(e) => setFormData((prev) => ({ ...prev, entryDuration: e.target.value }))}
                            />
                            <FormInput
                                helpText="e.g., 1d, 1w, 1mo"
                                id="timeHorizon"
                                label="Time Horizon"
                                value={formData.timeHorizon}
                                onChange={(e) => setFormData((prev) => ({ ...prev, timeHorizon: e.target.value }))}
                            />
                        </div>
                    </div>

                    <div className="mt-6 flex flex-wrap items-center gap-2 justify-end">
                        {isDirty && canEditStrategy && <span className="mr-1 text-xs text-amber-300/90">Unsaved changes</span>}
                        {!isCreateMode && isOwnedStrategy && (
                            <Button
                                disabled={!strategyId || !formData.active}
                                type="button"
                                variant="danger"
                                onClick={() => setShowDeactivateConfirm(true)}
                            >
                                Deactivate
                            </Button>
                        )}
                        <Button
                            disabled={!canEditStrategy || !isDirty || createMutation.isPending || updateMutation.isPending}
                            type="button"
                            variant="primary"
                            onClick={handleSave}
                        >
                            {createMutation.isPending || updateMutation.isPending ? 'Saving…' : 'Save changes'}
                        </Button>
                    </div>
                </fieldset>
            </Card>

            {!isCreateMode && strategyId && showShareModal && (
                <ShareResourceModal
                    isOpen={showShareModal}
                    resourceLabel={loadedStrategy?.name ?? `Option Buying Strategy #${strategyId}`}
                    resourcePk={strategyId}
                    resourceType="optionBuyingStrategy"
                    onClose={() => setShowShareModal(false)}
                />
            )}

            <ConfirmationDialog
                cancelText="Cancel"
                confirmText="Deactivate"
                isOpen={showDeactivateConfirm}
                message="This will mark the option buying strategy inactive. Existing backtests/trades will remain intact."
                title="Deactivate strategy?"
                onCancel={() => setShowDeactivateConfirm(false)}
                onConfirm={handleDeactivate}
            />
        </UserLayout>
    );
};

export default OptionBuyingStrategyEditor;
